import React, { FC, useState, useEffect, useRef } from "react";
import moment from "moment";
import Highlighter from "react-highlight-words";
import { cloneDeep, map, get, filter, isEmpty } from "lodash";
import { Radio, DatePicker, Select, Input, Table, Drawer, Button, Row, Col, Tooltip, Pagination, Dropdown, Checkbox, Menu, Space } from "antd";
import { CaretDownOutlined, SearchOutlined } from "@ant-design/icons";

// include local dependencies
import TableConfig from "@components/tableConfigDrawer";
import DownloadSection from "./downloadSection";
import { dateFormatConfig } from "@utils/constants";
// import { useMemberListStore } from "@stores/useMemberListStore";
import { formatDisplayDate, getClientName } from "@utils/helpers";
import { TMembersProps } from "@pages/members/constants";
import DashboardService from "@services/dashboard.service";
import "./inpatientReport.scss";
import MyCareLogo from "@assets/images/myCareLogo.png";
import DemoLogo from "@assets/images/dashboardReport.png";
import classNames from "classnames";
import { ASC, DESC } from "@utils/constants";
import { TableLocale } from "@components/noDataFound";
/********************************************** */

const { Option } = Select;
const { RangePicker } = DatePicker;

const rowsPerPageArray: any[] = ["5", "10", "15", "20", "25", "30", "35", "40", "45", "50"];

const InpatientManagementReport: FC<TMembersProps> = () => {
  const [data, setMemberListData] = useState<any>([]);
  const [rowsPerPage, setRowsPerPage] = useState<any>(5);
  const [totalPages, setTotalPages] = useState<any>(0);
  const [currentPage, setCurrentPage] = useState<any>(1);
  const [reminderStartFilter, setReminderStartFilter] = useState<any>(moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format("YYYY-MM-DD"));
  const [reminderEndFilter, setReminderEndFilter] = useState<any>(moment().format("YYYY-MM-DD"));
  const [sortType, setSortTypeFilter] = useState<any>(undefined);
  const [sortBy, setSortByFilter] = useState<any>(undefined);
  const [loading, setLoaderFlag] = useState(false);
  const [searchBy, setSearchBy] = useState<any>(undefined);
  const [columnListData, setColumnListData] = useState<any>([]);
  const [searchKeywordNew, setSearchKeywordNew] = useState<any>([]);
  const [columnSearchInfo, setColumnSearchInfo] = useState<any>({});
  const [searchByPage, setSearchByPage] = useState<any>(1);
  const [rowsPerPageToggle, setRowsPerPageToggle] = useState<boolean>(false);
  const [sortedInfo, setSortedInfo] = useState<any>({});
  const [isOpenDrawer, toggleDrawerHandler] = useState(false);
  const [isDownloadDrawerOpen, setDownloadDrawer] = useState(false);
  const [selectionType] = useState<"checkbox" | "radio">("checkbox");
  const [selectedRows, setSelectedRows] = useState<any>({
    selectedRowKeys: [],
  });
  const [tableConfigData, setTableConfigData] = useState<any>();
  const [tempTableConfig, tempTableConfigHandler] = useState<any>();
  const [downloadType, setDownloadType] = useState();
  const [toggleSelectAllRow, setToggleSelectAllRow] = useState(false);
  const [totalCount, setTotalCount] = useState();
  const [downloadColData, setDownloadColData] = useState<any>([]);

  const TablePreloader = () => {
    return (
      <div className="table-preloader">
        {map(Array.from(Array(rowsPerPage + 1).keys()), (item) => {
          return (
            <div className="table-preloader__row" key={"row_" + item}>
              {map(Array.from(Array(8).keys()), (item) => {
                return (
                  <div key={"col_" + item} className="table-preloader__row__column">
                    <div className="table-preloader__row__column__title shimmerBG"></div>
                    <div className="table-preloader__row__column__sub-title shimmerBG"></div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const handleReminderDateFilter = (range: any) => {
    const reminderStartDate = range && range.length > 0 ? range[0].format("YYYY-MM-DD") : undefined;
    const reminderEndDate = range && range.length > 0 ? range[1].format("YYYY-MM-DD") : undefined;
    setReminderStartFilter(reminderStartDate);
    setReminderEndFilter(reminderEndDate);

    getMembers({
      page: getCurrentPage(),
      limit: rowsPerPage,
      sortBy: sortBy,
      sortType: sortType,
      searchOn: searchKeywordNew,
      reminderStartDate: reminderStartDate,
      reminderEndDate: reminderEndDate,
    });
  };

  const updateSelection = ({ memData = data, toggleRequired = true }) => {
    onSelectChange(memData.map((p: any) => p.member_id));
    if (toggleRequired) {
      setToggleSelectAllRow(!toggleSelectAllRow);
    }
  };

  const getCurrentPage = (_searchBy = searchBy) => {
    if (_searchBy) {
      return searchByPage;
    }

    return currentPage;
  };

  const getMembers = ({ page, limit, sortBy, sortType, searchOn, reminderStartDate, reminderEndDate }: any) => {
    setLoaderFlag(true);

    return DashboardService.fetchInpatientManagementReportData({ startDate: reminderStartDate, endDate: reminderEndDate, page, limit, searchOn, sortBy, sortType }).subscribe({
      next: (v) => {
        setMemberListData([]);
        const memberData = v?.data?.data?.records;
        setTotalCount(v?.data?.data?.count);
        setMemberListData(
          memberData.map((patient: any) => {
            return { ...patient, key: patient?.member_id };
          })
        );
        if (downloadType === "all") {
          updateSelection({ memData: memberData, toggleRequired: false });
        }
        setTotalPages(v.data.data.count);
        setLoaderFlag(false);
      },
      error: (e) => {
        console.log(e);
        setLoaderFlag(false);
      },
    });
  };

  const getColumnList = () => {
    DashboardService.getInpatientManagementReportColumnList().subscribe({
      next: (v) => {
        const colData = v?.data?.data?.columns;
        setColumnListData(colData);
        const tcf = cloneDeep(colData)?.map((item: any) => {
          let newItem = {
            ...item,
            title: item.tableHeaderName,
            label: item.displayName,
            sequence: item.order,
            width: 200,
            sortOrder: sortedInfo?.columnKey === item.dataIndex ? sortedInfo.order : undefined,
            ellipsis: {
              showTitle: false,
            },
            colorCoded: false,
            sorter: () => {},
            render: (text: any) => (
              <Tooltip placement="topLeft" title={text}>
                {text}
              </Tooltip>
            ),
          };

          if (Object.keys(sortedInfo).length !== 0) {
            newItem = { ...newItem, sortOrder: sortedInfo?.columnKey === item.dataIndex ? sortedInfo.order : undefined };
          }

          return newItem;
        });
        setTableConfigData(tcf);
        tableConfigCallback(tcf);
        setDownloadColData([...tcf, ...colData.filter((col: any) => col.tableHeaderName)]);
      },
      error: (e) => {
        setColumnListData([]);
        setDownloadColData([]);
      },
    });
  };

  useEffect(() => {
    console.log("TablePreloader.useEffect");
    getMembers({
      page: getCurrentPage(),
      limit: rowsPerPage || 5,
      sortBy: sortBy,
      sortType: sortType,
      searchOn: searchKeywordNew,
      reminderStartDate: reminderStartFilter,
      reminderEndDate: reminderEndFilter,
    });

    getColumnList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let sub: any;

  const applyColumnSearch = (columnName: any, text: any, resetFlag?: boolean) => {
    text = text ? text : get(columnSearchInfo, [columnName], "");
    let sBy: string | undefined = undefined;
    let sKeyword = undefined;
    if (columnName?.trim() !== "" || text.trim() !== "") {
      sBy = columnName;
      sKeyword = text;
    }
    sub?.unsubscribe();
    setSearchByPage(1);

    let searchOn = filter(searchKeywordNew, (item) => {
      return item.columnName !== sBy;
    });
    let x = resetFlag === true ? searchOn : [...searchOn, { columnName: sBy, searchText: sKeyword }];
    setSearchKeywordNew(x);
    console.log("x", x);
    sub = getMembers({
      page: getCurrentPage(),
      limit: rowsPerPage,
      sortBy: sortBy,
      sortType: sortType,
      searchOn: x,
      reminderStartDate: reminderStartFilter,
      reminderEndDate: reminderEndFilter,
    });
    setSearchBy(sBy);
  };

  const drawerViewType = {
    label: "Select column to display",
    key: "columnConfig",
    width: "580",
    padding: 16,
    className: "tableConfigDrawer",
  };

  const cancelSelection = (event: any) => {
    setDownloadType(undefined);
    onSelectChange([]);
    setToggleSelectAllRow(!toggleSelectAllRow);
  };

  const selectionMenu = (
    <Menu>
      <div className="table-selection-options">
        <Radio.Group value={downloadType ? downloadType : "current"} onChange={(e) => setDownloadType(e.target.value)}>
          <Space direction="vertical">
            <Radio value="current" className="f16 clr-n100 selection-options">
              All members from the current page
            </Radio>
            <Radio value="all" className="f16 clr-n100 selection-options">
              All members across all pages
            </Radio>
          </Space>
        </Radio.Group>
        <div className="selection-actions">
          <Button className="cancel-btn" onClick={cancelSelection}>
            Cancel
          </Button>
          <Button type="primary" onClick={() => updateSelection({})}>
            Continue
          </Button>
        </div>
      </div>
    </Menu>
  );

  const { selectedRowKeys } = selectedRows;

  const onSelectChange = (selectedRowKeys: any) => {
    setSelectedRows({ selectedRowKeys });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    columnTitle: (
      <div className="selection-column">
        <Dropdown overlay={toggleSelectAllRow ? selectionMenu : <></>} visible={toggleSelectAllRow}>
          <Checkbox
            checked={selectedRowKeys?.length === data?.length}
            onClick={() => setToggleSelectAllRow(!toggleSelectAllRow)}
            onChange={() => onSelectChange(selectedRowKeys?.length === data?.length ? [] : data.map((d: any) => d?.key))}
            indeterminate={selectedRowKeys.length > 0 && selectedRowKeys?.length !== data.length}
          />
        </Dropdown>
      </div>
    ),
  };

  const toggleDrawer = () => {
    toggleDrawerHandler(!isOpenDrawer);
  };

  const toggleDownloadDrawer = () => {
    setDownloadDrawer((prevDrawerState) => !prevDrawerState);
  };

  const updateTableConfig = () => {
    const columns: any = [];
    tempTableConfig?.forEach((col: any) => {
      const arr = columnListData.filter((cld: any) => {
        return col.dataIndex === cld.dataIndex;
      });
      if (arr && arr.length > 0 && col.isSelected) columns.push({ column_id: arr[0].column_id, order: col.sequence });
    });
    DashboardService.updateInpatientManagementReportColumnList({ columns }).subscribe({
      next: (v) => {
        setTableConfigData(tempTableConfig);
        getColumnList();
      },
      error: (e) => {},
    });

    toggleDrawer();
  };

  // Update data in temp storage to update later
  const tableConfigCallback = (data: any) => {
    tempTableConfigHandler(data);
  };

  const setPageSize = (e: number) => {
    setRowsPerPage(e);
    setCurrentPage(1);

    getMembers({
      page: 1,
      limit: e,
      sortBy: sortBy,
      sortType: sortType,
      searchOn: searchKeywordNew,
      reminderStartDate: reminderStartFilter,
      reminderEndDate: reminderEndFilter,
    });
  };

  const changeCurrentPage = (val: any) => {
    if (searchBy) {
      setSearchByPage(val);

      getMembers({
        page: val,
        limit: rowsPerPage,
        sortBy: sortBy,
        sortType: sortType,
        searchOn: searchKeywordNew,
        reminderStartDate: reminderStartFilter,
        reminderEndDate: reminderEndFilter,
      });
      return;
    }
    setCurrentPage(val);

    getMembers({
      page: val,
      limit: rowsPerPage,
      sortBy: sortBy,
      sortType: sortType,
      searchOn: searchKeywordNew,
      reminderStartDate: reminderStartFilter,
      reminderEndDate: reminderEndFilter,
    });
  };

  const drawerFooter = () => {
    return (
      <div style={{ textAlign: "right" }} className="cancel-button">
        <Button type="text" size="large" onClick={onCloseConfigDrawer}>
          Cancel
        </Button>

        <Button type="primary" onClick={updateTableConfig} style={{ height: "40px" }}>
          Update Table
        </Button>
      </div>
    );
  };

  const drawerView = () => {
    return <TableConfig configUpdatehandler={tableConfigCallback} tableConfigData={cloneDeep(tempTableConfig)} />;
  };

  const onCloseConfigDrawer = () => {
    toggleDrawer();
    resetDefaultTableConfig({ action: "revert" });
  };

  const removeSortOrderFromResponse = (response: any) => {
    return response.map((column: any) => {
      const { sortOrder, ...columnWithoutSortOrder } = column;
      return columnWithoutSortOrder;
    });
  };

  const handleChange = (pagination: any, filters: any, sorter: any, extra: any) => {
    if (extra.action === "sort") {
      setSortedInfo(sorter);
      const tempConfig = cloneDeep(tableConfigData)?.map((data: any) => {
        if (data.dataIndex === sorter.columnKey) {
          data.sortOrder = sorter.order;
        }
        return data;
      });
      const tempTableConfig = cloneDeep(tableConfigData)?.map((data: any) => {
        if (data.dataIndex === sorter.columnKey) {
          data.sortOrder = sorter.order;
        }
        return data;
      });

      setTableConfigData(tempConfig);
      tempTableConfigHandler(tempTableConfig);
      let sortType = sorter?.order === "ascend" ? ASC : sorter?.order === "descend" ? DESC : sorter?.order;
      if (sortType) {
        setTableConfigData(removeSortOrderFromResponse(tableConfigData));
      } else {
        tableConfigData?.forEach((element: any) => {
          setTableConfigData(tableConfigData);
        });
      }
      setSortByFilter(sorter?.columnKey);
      setSortTypeFilter(sortType);

      getMembers({
        page: getCurrentPage(),
        limit: rowsPerPage,
        sortBy: sorter?.columnKey,
        sortType: sortType,
        searchOn: searchKeywordNew,
        reminderStartDate: reminderStartFilter,
        reminderEndDate: reminderEndFilter,
      });
    }
  };

  const highlighterDiv = (item: any, text: any, record: any, searchWord: any) => {
    switch (item.dataIndex) {
      case "dob":
      case "inpatient_case_reviewed":
      case "admission_date":
      case "discharge_date":
      case "last_office_visit":
      case "last_admission_date":
      case "next_office_visit":
        return (
          <span>
            <Tooltip placement="topLeft" title={text ? formatDisplayDate(text, "createDateFormat") : ""}>
              <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchWord]}
                autoEscape
                textToHighlight={!isEmpty(text) ? `${formatDisplayDate(text, "createDateFormat")}` : "--"}
              />
            </Tooltip>
          </span>
        );
      default:
        return (
          <Tooltip placement="topLeft" title={text?.toString() ?? ""}>
            <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={!isEmpty(text?.toString()) ? text?.toString() : "--"} />
          </Tooltip>
        );
    }
  };

  const inputRef = useRef<any>(null);
  const SearchFilter = (item: any) => {
    return {
      ...item,
      filterDropdown: ({ confirm }: any) => (
        <div style={{ padding: "10px" }}>
          <Input
            ref={inputRef}
            style={{ marginBottom: 8, display: "block" }}
            value={get(columnSearchInfo, item.dataIndex, "")}
            onChange={(e) => {
              setColumnSearchInfo({
                ...columnSearchInfo,
                [item.dataIndex]: e.target.value,
              });
            }}
            onPressEnter={(e) => {
              applyColumnSearch(item.dataIndex, "");
              confirm({ closeDropdown: true });
              e.stopPropagation();
            }}
          />
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
              onClick={(e) => {
                setColumnSearchInfo({
                  ...columnSearchInfo,
                  [item.dataIndex]: get(columnSearchInfo, item.dataIndex, ""),
                });
                applyColumnSearch(item.dataIndex, "");
                confirm({ closeDropdown: true });
              }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                setColumnSearchInfo({ ...columnSearchInfo, [item.dataIndex]: "" });
                applyColumnSearch(item.dataIndex, "", true);
                confirm({ closeDropdown: true });
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: () => (
        <SearchOutlined
          style={{
            color: get(columnSearchInfo, item.dataIndex, "") !== "" ? "#EF6D2A" : undefined,
          }}
        />
      ),
      render: (text: any, record: any) => highlighterDiv(item, text, record, get(columnSearchInfo, item.dataIndex, "")),
      onFilter: (value: any, record: any) => (record[item.dataIndex] ? record[item.dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : ""),
      onFilterDropdownOpenChange: (visible: any) => {
        if (visible) {
          setTimeout(() => inputRef.current?.select(), 100);
        }
      },
    };
  };

  const _xyz: Array<{}> = map(cloneDeep(tableConfigData), (dataInfo: any) => {
    return SearchFilter(dataInfo);
  });

  let headerLogo = MyCareLogo;

  if (process.env.REACT_APP_ENVIRONMENT === "demo") {
    headerLogo = DemoLogo;
  }

  const _classNames = classNames("ui-inpatient-management", {
    "ui-inpatient-management--no-scroll": toggleSelectAllRow,
  });

  const disabledDate = (current: any) => {
    // Can not select days before today and today
    return current && current <= moment("01-10-2022", "DD-MM-YYYY").subtract(1, "days");
  };

  const resetDefaultTableConfig = ({ action = "default" }) => {
    if (action === "default") {
      let columnData = cloneDeep(tempTableConfig);
      // let defaultColCount = 1;
      columnData = columnData?.map((col: any) => {
        if (col.isDraggable) {
          if (col.isDefault === "Y") {
            col.sequence = col.defaultDisplayOrder;
            col.isSelected = true;
            col.draggable = true;
          } else {
            col.sequence = 0;
            col.isSelected = false;
            col.draggable = false;
          }
        } else {
          col.sequence = col.order;
          col.isSelected = true;
          col.draggable = true;
        }
        return col;
      });
      tempTableConfigHandler(columnData);
    } else {
      tempTableConfigHandler(tableConfigData);
    }
  };

  const drawerHeader = () => {
    return (
      <Button type="link" className="clr-blueLink" style={{ fontWeight: "normal" }} onClick={() => resetDefaultTableConfig({})}>
        Reset to default View
      </Button>
    );
  };

  return (
    <>
      {toggleSelectAllRow ? <div className="ui-inpatient-management-backdropOnRowSelection" /> : null}
      <div className={_classNames}>
        <Row className="ui-inpatient-management__header">
          <div className="ui-inpatient-management__header__left">
            <img src={headerLogo} alt="company logo" className="ui-inpatient-management__header__left__icon" />
            <div className="ui-inpatient-management__header__left__heading">Inpatient Management Report</div>
          </div>

          <div style={{ alignItems: "center" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <span className="ui-inpatient-management__header__right__label">{`( * ) User Input Fields`}</span>
              <Tooltip title="Download XLS">
                <i className="btn-icon icon-download clr-n125" style={{ width: "20px", height: "20px" }} onClick={toggleDownloadDrawer} />
              </Tooltip>

              <i style={{ marginRight: 8 }} className="btn-icon icon-settings clr-n125" onClick={() => toggleDrawer()} />

              <div className="reminderDatePickerKeyInsight">
                <div className="reminderDateLabel">
                  <span className="icon-calendar f18" />
                </div>
                <RangePicker
                  className="ui-inpatient-management__header__range-picker"
                  picker="date"
                  allowClear={false}
                  format={dateFormatConfig.get(getClientName())["defaultDateFormat"]}
                  value={reminderStartFilter && reminderEndFilter ? [moment(moment(reminderStartFilter).format("MM-DD-YYYY")), moment(moment(reminderEndFilter).format("MM-DD-YYYY"))] : undefined}
                  dropdownClassName="reminder-picker"
                  ranges={{
                    "More Filters:": [null, null],
                    "Last 1 Day": [moment().subtract(1, "days"), moment()],
                    "Last 7 Days": [moment().subtract(7, "days"), moment()],
                    "Current Month to Date": [moment(moment().startOf("month")), moment()],
                  }}
                  onChange={(range) => {
                    handleReminderDateFilter(range);
                  }}
                  disabledDate={disabledDate}
                />
              </div>
            </div>
          </div>
        </Row>
        <Row style={{ marginTop: 10 }} className={`ui-inpatient-management__tableContainer ${loading ? "dnone" : ""}`}>
          <Table
            id="member-table"
            bordered={true}
            tableLayout="fixed"
            pagination={false}
            onChange={handleChange}
            scroll={{ y: window.innerHeight - 420 }}
            rowSelection={
              data.length === 0
                ? undefined
                : {
                    type: selectionType,
                    ...rowSelection,
                  }
            }
            columns={_xyz
              ?.sort((a: any, b: any) => a.sequence - b.sequence)
              .filter((item: any) => {
                return item.isSelected;
              })}
            dataSource={data}
            locale={TableLocale}
          />
          <div className="ui-inpatient-management__tableContainer__pagination">
            <div>
              Show
              <Select
                defaultValue={rowsPerPage}
                style={{ width: 64, margin: "8px 8px" }}
                onClick={() => setRowsPerPageToggle(!rowsPerPageToggle)}
                onChange={(e) => setPageSize(e)}
                open={rowsPerPageToggle}
                suffixIcon={<CaretDownOutlined />}
              >
                {rowsPerPageArray.map((val) => (
                  <Option key={val} value={val}>
                    {val}
                  </Option>
                ))}
              </Select>
              rows per page
            </div>
            {data?.length > 0 && (
              <Pagination
                total={totalPages}
                showTotal={(total: any, range: any) => (
                  <>
                    <span style={{ color: "#091e42" }}>
                      {rowsPerPage * getCurrentPage() - (rowsPerPage - 1)} -{" "}
                      {rowsPerPage * getCurrentPage() > data?.length
                        ? data?.length + rowsPerPage * getCurrentPage() - (rowsPerPage - 1) - 1
                        : rowsPerPage * getCurrentPage() + rowsPerPage * getCurrentPage() - (rowsPerPage - 1) - 1}
                    </span>
                    <span> of {totalPages}</span>
                  </>
                )}
                key={rowsPerPage}
                defaultPageSize={rowsPerPage}
                defaultCurrent={getCurrentPage()}
                current={getCurrentPage()}
                showSizeChanger={false}
                onChange={(e: any) => {
                  changeCurrentPage(e);
                }}
              />
            )}
          </div>
        </Row>
        <Row className={`tableConainer ${!loading ? "dnone" : ""}`}>
          <Col span={24}>
            <div className="filters-row">
              <div className="reminderDatePicker reminderDatePicker_preloader shimmerBG"></div>
              <div className="btn-icon_preloader shimmerBG"></div>
              <div className="btn-icon_preloader shimmerBG"></div>
              <div className="btn-icon_preloader shimmerBG"></div>
              <div className="btn-icon_preloader shimmerBG" style={{ marginRight: 0 }}></div>
              <div className="btn-icon_preloader shimmerBG" style={{ marginLeft: 0 }}></div>
            </div>
            <TablePreloader />
            <div className="pagination">
              <div className="rowperpage_preloader shimmerBG"></div>
              <div className="pagination_text_preloader shimmerBG"></div>
            </div>
          </Col>
        </Row>
      </div>

      <Drawer
        destroyOnClose={true}
        title={drawerViewType.label}
        placement="right"
        closable={false}
        onClose={onCloseConfigDrawer}
        visible={isOpenDrawer}
        key="right"
        width={drawerViewType.width}
        bodyStyle={{ padding: drawerViewType.padding }}
        className={`membersListingDrawer ${drawerViewType.className}`}
        footer={drawerFooter()}
        extra={drawerHeader()}
      >
        {drawerView()}
      </Drawer>

      {/* download drawer */}
      <Drawer
        destroyOnClose={true}
        placement="bottom"
        closable={false}
        onClose={toggleDownloadDrawer}
        visible={isDownloadDrawerOpen}
        key="bottom"
        height="125px"
        maskClosable={true}
        className={"downloadDrawer"}
        mask={false}
      >
        <DownloadSection
          columns={tableConfigData}
          downloadType={downloadType}
          sortType={sortType}
          sortBy={sortBy}
          reminderStartFilter={reminderStartFilter}
          reminderEndFilter={reminderEndFilter}
          totalUserCount={totalCount}
          totalUser={data?.filter((d: { member_id: any }) => selectedRows?.selectedRowKeys?.includes(d.member_id))}
          onClose={toggleDownloadDrawer}
          searchOn={searchKeywordNew}
          downloadColData={downloadColData}
        />
      </Drawer>
    </>
  );
};

export default InpatientManagementReport;
